import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import SocialIcons from "@/components/SocialIcons";

const DEFAULTS = {
  brand_name: "VoraStyle",
  tagline: "The creative curation collective for fashion & interior design.",
  support_email: "Support@VoraStyle.com",
  copyright_text: "© 2026 VoraStyle · Curated interiors for aesthetic living",
  version_label: "v0.1 · Beta",
  facebook_url: "",
  instagram_url: "",
  pinterest_url: "",
  footer_links_explore: [
    { label: "Discover sets", to: "/discover" },
    { label: "The Archive", to: "/products" },
    { label: "Create a set", to: "/editor" },
    { label: "Browser extension", to: "/clipper" },
    { label: "Blog", to: "https://vorastyle.blogspot.com/", external: true },
  ],
  footer_links_company: [
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
    { label: "Tip the collective", to: "/support" },
    { label: "Privacy Policy", to: "/privacy" },
    { label: "Terms of Service", to: "/terms" },
  ],
};

export default function Footer() {
  const location = useLocation();
  const [s, setS] = useState(DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings")
      .then(({ data }) => { if (!cancelled && data) setS({ ...DEFAULTS, ...data }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Hide the footer inside the Editor (canvas needs the full viewport).
  if (location.pathname.startsWith("/editor")) return null;

  return (
    <footer className="mt-16 border-t border-[#EFEFEF] bg-white" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
        {/* Brand */}
        <div>
          <Link to="/" data-testid="footer-logo" className="font-heading text-2xl tracking-tight text-[#333333]">{s.brand_name}</Link>
          <p className="font-body text-sm text-[#595959] mt-3 max-w-xs leading-relaxed">{s.tagline}</p>
          <SocialIcons
            size="md"
            variant="boxed"
            className="mt-5"
            testidPrefix="footer-"
            urls={{
              facebook: s.facebook_url,
              instagram: s.instagram_url,
              pinterest: s.pinterest_url,
            }}
          />
        </div>

        {/* Explore */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333333] mb-4">Explore</div>
          <ul className="space-y-2.5 font-body text-sm text-[#595959]">
            {(s.footer_links_explore || []).map((l, i) => (
              <li key={`exp-${i}`}>
                {l.external || /^https?:\/\//i.test(l.to) ? (
                  <a
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-link-explore-${i}`}
                    className="hover:text-[#333333]"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} data-testid={`footer-link-explore-${i}`} className="hover:text-[#333333]">{l.label}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333333] mb-4">Company</div>
          <ul className="space-y-2.5 font-body text-sm text-[#595959]">
            {(s.footer_links_company || []).map((l, i) => (
              <li key={`co-${i}`}>
                {l.external || /^https?:\/\//i.test(l.to) ? (
                  <a
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-link-company-${i}`}
                    className="hover:text-[#333333]"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} data-testid={`footer-link-company-${i}`} className="hover:text-[#333333]">{l.label}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Support */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#333333] mb-4">Support</div>
          <p className="font-body text-sm text-[#595959] leading-relaxed">
            Questions, partnerships, or feedback?
          </p>
          <a
            href={`mailto:${s.support_email}`}
            data-testid="footer-support-email"
            className="inline-block mt-3 font-body text-sm text-[#333333] underline underline-offset-4 decoration-[#9CA47E] hover:text-[#9CA47E] break-all"
          >
            {s.support_email}
          </a>
        </div>
      </div>

      <div className="border-t border-[#EFEFEF]">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#595959]">
          <div data-testid="footer-copyright">{s.copyright_text}</div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-[#9CA47E]" />
            <span data-testid="footer-version">{s.version_label}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
